import { isSfwSite, WORKER_URL } from '../config.js';
import { CREATION_FLAT_CREDITS, CRYPTO_SPRITE_PRICES } from '../shared/crypto-packs.js';
import { fetchCache } from '../utils/fetchCache.js';
import { getUserCredits } from './videoScheduleApi.js';
import { scaleStandaloneImageCreditCost } from './videoPage/standaloneImageSize.js';

const DEFAULT_PRICING = {
  image_base: 10,
  image_base_anime: 10,
  image_base_realism: 20,
  image_lora_surcharge: 10,
  video_lowres_base: 100,
  video_hd_base: 100,
  video_lora_surcharge: 15,
  video_duration_min_seconds: 4,
  video_duration_max_seconds: 8,
  video_duration_anchor_seconds: 4,
  video_duration_anchor_credits: 80,
  video_credit_per_extra_second: 20,
  free_outfit_ids: [0, 1],
  sprites_per_outfit: { 0: 8, 1: 14, default: 8 },
};

let cachedPricing = null;

const stripeNumber = (value, fallback) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const durationBounds = (data) => ({
  video_duration_min_seconds: stripeNumber(data?.video_duration_min_seconds, 4),
  video_duration_max_seconds: stripeNumber(data?.video_duration_max_seconds, 8),
});

const normalizePricing = (data) => {
  const shared = {
    free_outfit_ids: Array.isArray(data?.free_outfit_ids) ? data.free_outfit_ids : [0, 1],
    sprites_per_outfit: data?.sprites_per_outfit ?? { 0: 8, 1: 14, default: 8 },
  };
  const bounds = durationBounds(data);
  if (!isSfwSite()) {
    return {
      image_base: CRYPTO_SPRITE_PRICES.image_base_anime,
      image_base_anime: CRYPTO_SPRITE_PRICES.image_base_anime,
      image_base_realism: CRYPTO_SPRITE_PRICES.image_base_realism,
      image_lora_surcharge: CRYPTO_SPRITE_PRICES.image_lora_surcharge,
      video_lowres_base: CRYPTO_SPRITE_PRICES.video_lowres_base,
      video_hd_base: CRYPTO_SPRITE_PRICES.video_hd_base,
      video_lora_surcharge: CRYPTO_SPRITE_PRICES.video_lora_surcharge,
      video_duration_anchor_seconds: CRYPTO_SPRITE_PRICES.video_duration_anchor_seconds,
      video_duration_anchor_credits: CRYPTO_SPRITE_PRICES.video_duration_anchor_credits,
      video_credit_per_extra_second: CRYPTO_SPRITE_PRICES.video_credit_per_extra_second,
      ...bounds,
      ...shared,
    };
  }
  return {
    image_base: stripeNumber(data?.image_base ?? data?.image_base_anime, 10),
    image_base_anime: stripeNumber(data?.image_base_anime ?? data?.image_base, 10),
    image_base_realism: stripeNumber(data?.image_base_realism, 20),
    image_lora_surcharge: stripeNumber(data?.image_lora_surcharge, 10),
    video_lowres_base: stripeNumber(data?.video_lowres_base, 100),
    video_hd_base: stripeNumber(data?.video_hd_base, 100),
    video_lora_surcharge: stripeNumber(data?.video_lora_surcharge, 15),
    video_duration_anchor_seconds: stripeNumber(data?.video_duration_anchor_seconds, 4),
    video_duration_anchor_credits: stripeNumber(data?.video_duration_anchor_credits, 80),
    video_credit_per_extra_second: stripeNumber(data?.video_credit_per_extra_second, 20),
    ...bounds,
    ...shared,
  };
};

export const loadCreditPricing = async () => {
  if (cachedPricing) return cachedPricing;
  try {
    const data = await fetchCache.fetchJSON(`${WORKER_URL}/credit-pricing`);
    cachedPricing = normalizePricing(data);
  } catch (error) {
    console.warn('[creditCostApi] loadCreditPricing failed, using defaults:', error);
    cachedPricing = normalizePricing(DEFAULT_PRICING);
  }
  return cachedPricing;
};

export const getCreditPricing = () => cachedPricing ?? normalizePricing(DEFAULT_PRICING);

export const normalizeImageStyle = (style) => (style === 'realism' ? 'realism' : 'anime');

export const resolveImageBase = (style, pricing = null) => {
  const p = pricing ?? getCreditPricing();
  if (normalizeImageStyle(style) === 'realism') {
    return Number(p.image_base_realism ?? p.image_base ?? 15);
  }
  return Number(p.image_base_anime ?? p.image_base ?? 10);
};

export const countVideoLoraEntries = (lora, max = 3) => {
  const entries = Array.isArray(lora) ? lora : (lora && typeof lora === 'object' ? [lora] : []);
  const count = entries.filter((entry) => {
    if (!entry || typeof entry !== 'object') return false;
    const id = entry.id == null ? '' : String(entry.id).trim();
    const source = typeof entry.source === 'string' ? entry.source.trim() : '';
    return id !== '' && source !== '';
  }).length;
  const cap = Number.isFinite(Number(max)) ? Math.max(0, Number(max)) : 3;
  return Math.min(cap, count);
};

export const computeStandaloneImageCost = ({
  style = 'anime',
  loraCount = 0,
  pricing = null,
  width = null,
  height = null,
}) => {
  const p = pricing ?? getCreditPricing();
  const surcharge = Number(p.image_lora_surcharge ?? 10);
  const count = Math.max(0, Math.min(3, Number(loraCount) || 0));
  const fullCost = Math.max(1, Math.round(resolveImageBase(style, p) + surcharge * count));
  if (width == null && height == null) return fullCost;
  return scaleStandaloneImageCreditCost(fullCost, width, height);
};

export { CREATION_FLAT_CREDITS };

export const starterSpriteCount = (gender, outfitId) => {
  if (gender === 'male') return 5;
  if (Number(outfitId) === 1) return 11;
  return 5;
};

export const computeCharacterCreationCost = ({
  gender = 'female',
  style = 'anime',
  nsfw = false,
  characterHasLora = false,
  pricing = null,
} = {}) => {
  const imageCount = starterSpriteCount(gender, 0) + (nsfw ? starterSpriteCount(gender, 1) : 0);
  const unitCost = computeImageUnitCost({
    characterHasLora,
    actionHasLora: false,
    style,
    pricing,
  });
  const flat = CREATION_FLAT_CREDITS;
  return {
    imageCount,
    unitCost,
    flat,
    total: unitCost * imageCount + flat,
  };
};

export const computeImageUnitCost = ({
  characterHasLora = false,
  actionHasLora = false,
  style = 'anime',
  pricing = null,
}) => {
  const p = pricing ?? getCreditPricing();
  let cost = resolveImageBase(style, p);
  const surcharge = Number(p.image_lora_surcharge ?? 10);
  if (characterHasLora) cost += surcharge;
  if (actionHasLora) cost += surcharge;
  return Math.max(1, Math.round(cost));
};

export const getCharacterHasLora = (characterId, characters = []) => {
  const character = characters.find((entry) => String(entry.id) === String(characterId));
  return Boolean(character?.hasLora);
};

export const getCharacterStyle = (characterId, characters = []) => {
  const character = characters.find((entry) => String(entry.id) === String(characterId));
  return normalizeImageStyle(character?.style);
};

export const getActionHasLora = (actionId, actionMappings = {}) => {
  const mapping = actionMappings[actionId] ?? actionMappings[String(actionId)];
  return Boolean(mapping?.hasLora);
};

export const computeImageBatchTotal = ({
  characterId,
  actionIds = [],
  characters = [],
  actionMappings = {},
  pricing = null,
}) => {
  const characterHasLora = getCharacterHasLora(characterId, characters);
  const style = getCharacterStyle(characterId, characters);
  return actionIds.reduce((total, actionId) => {
    const actionHasLora = getActionHasLora(actionId, actionMappings);
    return total + computeImageUnitCost({ characterHasLora, actionHasLora, style, pricing });
  }, 0);
};

export const videoDurationBounds = (pricing = null) => {
  const p = pricing ?? getCreditPricing();
  const minRaw = Number(p.video_duration_min_seconds ?? 4);
  const maxRaw = Number(p.video_duration_max_seconds ?? 8);
  const min = Number.isInteger(minRaw) && minRaw >= 1 ? minRaw : 4;
  const max = Number.isInteger(maxRaw) && maxRaw >= min ? maxRaw : Math.max(min, 8);
  return { min, max };
};

export const computeVideoUnitCost = ({
  operation = 'video_create',
  userVideoLora = null,
  loraCount = null,
  maxLoras = 3,
  durationSeconds = null,
  pricing = null,
}) => {
  const p = pricing ?? getCreditPricing();
  const surcharge = Number(p.video_lora_surcharge ?? 15);
  const cap = Number.isFinite(Number(maxLoras)) ? Math.max(0, Number(maxLoras)) : 3;
  const count = loraCount == null
    ? countVideoLoraEntries(userVideoLora, cap)
    : Math.max(0, Math.min(cap, Number(loraCount) || 0));
  let base = Number(p.video_hd_base);
  if (operation !== 'video_hd') {
    const anchorSeconds = Number(p.video_duration_anchor_seconds ?? 4);
    const anchorCredits = Number(p.video_duration_anchor_credits ?? p.video_lowres_base ?? 100);
    const perExtra = Number(p.video_credit_per_extra_second ?? 0);
    const seconds = durationSeconds == null ? 5 : Number(durationSeconds);
    base = anchorCredits + perExtra * (seconds - anchorSeconds);
  }
  return Math.max(1, Math.round(base + surcharge * count));
};

const spritesPerOutfitCount = (outfitId, pricing) => {
  const p = pricing ?? getCreditPricing();
  const map = p.sprites_per_outfit ?? {};
  const key = String(outfitId);
  if (map[key] != null) return Number(map[key]);
  if (map[outfitId] != null) return Number(map[outfitId]);
  return Number(map.default ?? 8);
};

export const computeOutfitBatchTotal = ({
  outfitIds = [],
  characterHasLora = false,
  style = 'anime',
  pricing = null,
}) => {
  const p = pricing ?? getCreditPricing();
  const freeOutfitIds = new Set((p.free_outfit_ids ?? [0, 1]).map(Number));
  const unitCost = computeImageUnitCost({ characterHasLora, actionHasLora: false, style, pricing: p });
  let total = 0;
  let quantity = 0;

  outfitIds.forEach((outfitId) => {
    const id = Number(outfitId);
    if (freeOutfitIds.has(id)) return;
    const spriteCount = spritesPerOutfitCount(id, p);
    total += unitCost * spriteCount;
    quantity += spriteCount;
  });

  return { total, quantity, unitCost };
};

export const buildLocalCreditResult = (total) => {
  const availableCredits = getUserCredits();
  return {
    ok: true,
    total,
    available_credits: availableCredits,
    has_enough_credits: total <= availableCredits,
  };
};

export const interpolateCreditTemplate = (template, values) => {
  if (!template) return '';
  return template.replace(/\{(\w+)\}/g, (_, key) => String(values[key] ?? ''));
};

export const formatCreditsUsageLine = ({ total, available, template, unitCount = 1 }) => {
  const availableCredits = available ?? getUserCredits();
  if (template) {
    return interpolateCreditTemplate(template, {
      total,
      available: availableCredits,
      cost: total,
      count: unitCount,
    });
  }
  return `This will use ${total} / ${availableCredits} credits`;
};
